import React, { useState, useMemo } from 'react';
import { Search, X, ChevronLeft, ChevronRight, Calendar, Filter, Eye } from 'lucide-react';
import { useOrderHistory } from '../../hooks/useOrderHistory';
import { Modal, Button } from '../../components/ui';
import type { Order, OrderItem } from '../../types/@types/order';

// ── Helpers ────────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function formatCOP(v: number) {
  return '$ ' + Math.round(v).toLocaleString('es-CO');
}
function formatDateTime(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Status badges ──────────────────────────────────────────────────────────────

const KITCHEN_LABELS: Record<string, string> = {
  WAITING: 'En espera',
  IN_PRODUCTION: 'Preparando',
  DONE: 'Listo',
  DELIVERED: 'Entregado',
  CANCELLED: 'Cancelado',
};
const KITCHEN_COLORS: Record<string, string> = {
  WAITING: 'bg-amber-100 text-amber-800',
  IN_PRODUCTION: 'bg-blue-100 text-blue-800',
  DONE: 'bg-green-100 text-green-800',
  DELIVERED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
};
const PAYMENT_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  PARTIAL: 'Parcial',
  PAID: 'Pagado',
};
const PAYMENT_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  PARTIAL: 'bg-blue-100 text-blue-800',
  PAID: 'bg-emerald-100 text-emerald-800',
};
const FULFILLMENT_LABELS: Record<string, string> = {
  DINE_IN: 'Mesa',
  MESA: 'Mesa',
  TAKEOUT: 'Para llevar',
  TAKEAWAY: 'Para llevar',
  DELIVERY: 'Domicilio',
};

function KitchenBadge({ status }: { status?: string }) {
  const label = KITCHEN_LABELS[status ?? ''] ?? status ?? '—';
  const color = KITCHEN_COLORS[status ?? ''] ?? 'bg-muted text-text-muted';
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${color}`}>{label}</span>;
}
function PaymentBadge({ status }: { status?: string }) {
  const label = PAYMENT_LABELS[status ?? ''] ?? status ?? '—';
  const color = PAYMENT_COLORS[status ?? ''] ?? 'bg-muted text-text-muted';
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${color}`}>{label}</span>;
}

// ── Order Detail Modal ─────────────────────────────────────────────────────────

function getItemLineTotal(item: OrderItem): number {
  if (typeof item.line_subtotal === 'number') return item.line_subtotal;
  const qty = item.quantity ?? 0;
  const price = item.base_price ?? (typeof item.product !== 'string' ? (item.product as any)?.price ?? 0 : 0);
  return qty * price;
}

function OrderDetailModal({ order, onClose }: { order: Order | null; onClose: () => void }) {
  if (!order) return null;

  const fulfillment = FULFILLMENT_LABELS[order.fulfillmentType ?? order.destination ?? ''] ?? '—';
  const tableName = (order.table as any)?.name ?? (order.table as any)?.number
    ? `Mesa ${(order.table as any)?.name ?? (order.table as any)?.number}`
    : null;

  return (
    <Modal open={!!order} onClose={onClose} titleId="order-detail-modal" className="max-w-2xl">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-border px-6 py-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Pedido #{order.id}</p>
          <h2 className="text-lg font-semibold text-text">
            {order.customerName?.trim() || tableName || 'Sin nombre'}
          </h2>
          <p className="text-xs text-text-muted mt-0.5">{formatDateTime(order.createdAt)}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-9 w-9 rounded-full">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Meta */}
      <div className="flex flex-wrap gap-3 border-b border-border px-6 py-3">
        <div className="flex items-center gap-2 text-sm text-text-muted">
          <span className="font-medium text-text">Tipo:</span>
          <span>{fulfillment}</span>
        </div>
        {tableName && (
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <span className="font-medium text-text">Ubicación:</span>
            <span>{tableName}</span>
          </div>
        )}
        {order.customerPhone && (
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <span className="font-medium text-text">Tel:</span>
            <span>{order.customerPhone}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <KitchenBadge status={order.status} />
          <PaymentBadge status={order.payment_status} />
        </div>
      </div>

      {/* Items */}
      <div className="max-h-[50vh] overflow-y-auto px-6 py-4">
        {(order.items ?? []).length === 0 ? (
          <p className="text-sm text-text-muted">Sin productos</p>
        ) : (
          <ul className="space-y-3">
            {(order.items ?? []).map((item) => {
              const productName =
                (item as any).name ??
                (typeof item.product === 'object' ? (item.product as any)?.name : String(item.product)) ??
                'Producto';
              const additions = Object.entries(item.additions ?? {});
              const exceptions = item.exceptions ?? [];
              return (
                <li key={item.id} className="flex items-start justify-between gap-3 rounded-xl border border-border px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-text">
                      {item.quantity} × {productName}
                    </p>
                    {item.notes && <p className="mt-0.5 text-xs text-text-muted">Nota: {item.notes}</p>}
                    {additions.length > 0 && (
                      <p className="mt-0.5 text-xs text-text-muted">
                        Adiciones: {additions.map(([k, v]) => (Number(v) > 1 ? `${v}× ${k}` : k)).join(', ')}
                      </p>
                    )}
                    {exceptions.length > 0 && (
                      <p className="mt-0.5 text-xs text-text-muted">Sin: {exceptions.join(', ')}</p>
                    )}
                  </div>
                  <p className="shrink-0 text-sm font-semibold text-text">{formatCOP(getItemLineTotal(item))}</p>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-border px-6 py-4">
        {order.note && <p className="text-xs text-text-muted">Nota: {order.note}</p>}
        <div className="ml-auto text-right">
          <p className="text-xs text-text-muted">Total del pedido</p>
          <p className="text-xl font-bold text-text">{formatCOP(order.total ?? 0)}</p>
        </div>
      </div>
    </Modal>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

const PAYMENT_STATUS_OPTIONS = [
  { value: '', label: 'Todos los pagos' },
  { value: 'PENDING', label: 'Pendiente' },
  { value: 'PARTIAL', label: 'Parcial' },
  { value: 'PAID', label: 'Pagado' },
];

const FULFILLMENT_OPTIONS = [
  { value: '', label: 'Todos los tipos' },
  { value: 'MESA', label: 'Mesa' },
  { value: 'TAKEAWAY', label: 'Para llevar' },
  { value: 'DELIVERY', label: 'Domicilio' },
];

export default function OrderHistory() {
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(today());
  const [paymentStatus, setPaymentStatus] = useState('');
  const [fulfillmentType, setFulfillmentType] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);

  // Debounce search
  const searchTimeout = React.useRef<ReturnType<typeof setTimeout>>();
  function handleSearchChange(val: string) {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 350);
  }

  const params = useMemo(() => ({
    from,
    to,
    payment_status: paymentStatus || undefined,
    fulfillment_type: fulfillmentType || undefined,
    search: debouncedSearch || undefined,
    page,
    per_page: 20,
  }), [from, to, paymentStatus, fulfillmentType, debouncedSearch, page]);

  const { data, isLoading, isError } = useOrderHistory(params);

  const orders = data?.data ?? [];
  const totalPages = data?.last_page ?? 1;

  function resetFilters() {
    setFrom(daysAgo(6));
    setTo(today());
    setPaymentStatus('');
    setFulfillmentType('');
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
  }

  return (
    <div className="min-h-screen bg-bg p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-6">

        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Historial de Pedidos</h1>
            <p className="text-sm text-text-muted">
              {data ? `${data.total} pedido${data.total === 1 ? '' : 's'} encontrado${data.total === 1 ? '' : 's'}` : 'Cargando…'}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-2xl border border-border bg-surface p-4 shadow-card space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Calendar size={17} className="text-text-muted" />
            <label className="text-sm font-medium text-text-muted">Desde</label>
            <input
              type="date" value={from} max={to}
              onChange={e => { setFrom(e.target.value); setPage(1); }}
              className="rounded-lg border border-border bg-bg px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <label className="text-sm font-medium text-text-muted">Hasta</label>
            <input
              type="date" value={to} min={from} max={today()}
              onChange={e => { setTo(e.target.value); setPage(1); }}
              className="rounded-lg border border-border bg-bg px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="ml-auto flex gap-2">
              {[
                { label: 'Hoy', from: today(), to: today() },
                { label: '7d', from: daysAgo(6), to: today() },
                { label: '30d', from: daysAgo(29), to: today() },
              ].map(p => (
                <button
                  key={p.label}
                  onClick={() => { setFrom(p.from); setTo(p.to); setPage(1); }}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${from === p.from && to === p.to ? 'bg-primary text-white' : 'bg-muted text-text-muted hover:bg-primary/10'}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative min-w-[180px] flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                placeholder="Buscar cliente, teléfono o #pedido…"
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                className="w-full rounded-lg border border-border bg-bg py-1.5 pl-8 pr-3 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {search && (
                <button onClick={() => handleSearchChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text">
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Payment status */}
            <select
              value={paymentStatus}
              onChange={e => { setPaymentStatus(e.target.value); setPage(1); }}
              className="rounded-lg border border-border bg-bg px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {PAYMENT_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {/* Fulfillment */}
            <select
              value={fulfillmentType}
              onChange={e => { setFulfillmentType(e.target.value); setPage(1); }}
              className="rounded-lg border border-border bg-bg px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {FULFILLMENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-bg px-3 py-1.5 text-xs font-medium text-text-muted hover:text-text transition"
            >
              <Filter size={13} />
              Limpiar
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-border bg-surface shadow-card overflow-hidden">
          {isLoading ? (
            <div className="space-y-0">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 border-b border-border px-6 py-4">
                  <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                  <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                  <div className="h-4 w-20 animate-pulse rounded bg-muted ml-auto" />
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-amber-700">
              <p className="font-semibold">Error al cargar los pedidos</p>
              <p className="text-xs text-text-muted">Verifica tu conexión e intenta de nuevo</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16">
              <p className="text-base font-semibold text-text">Sin pedidos</p>
              <p className="text-sm text-text-muted">Prueba ajustando los filtros</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">#</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">Fecha</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">Cliente / Mesa</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">Tipo</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">Estado</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">Pago</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-text-muted">Total</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, idx) => {
                      const tableName = (order.table as any)?.name ?? ((order.table as any)?.number ? `Mesa ${(order.table as any).number}` : null);
                      const customerLabel = order.customerName?.trim() || tableName || '—';
                      const fulfillment = FULFILLMENT_LABELS[order.fulfillmentType ?? order.destination ?? ''] ?? '—';
                      return (
                        <tr
                          key={order.id}
                          className={`border-b border-border transition hover:bg-muted/30 cursor-pointer ${idx % 2 === 1 ? 'bg-muted/10' : ''}`}
                          onClick={() => setDetailOrder(order)}
                        >
                          <td className="px-5 py-3.5 font-mono text-xs text-text-muted">#{order.id}</td>
                          <td className="px-5 py-3.5 text-text-muted text-xs">{formatDateTime(order.createdAt)}</td>
                          <td className="px-5 py-3.5 font-medium text-text">{customerLabel}</td>
                          <td className="px-5 py-3.5 text-text-muted text-xs">{fulfillment}</td>
                          <td className="px-5 py-3.5"><KitchenBadge status={order.status} /></td>
                          <td className="px-5 py-3.5"><PaymentBadge status={order.payment_status} /></td>
                          <td className="px-5 py-3.5 text-right font-semibold text-text">{formatCOP(order.total ?? 0)}</td>
                          <td className="px-5 py-3.5">
                            <button className="rounded-lg p-1.5 text-text-muted hover:bg-primary/10 hover:text-primary transition">
                              <Eye size={15} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="divide-y divide-border md:hidden">
                {orders.map(order => {
                  const tableName = (order.table as any)?.name ?? ((order.table as any)?.number ? `Mesa ${(order.table as any).number}` : null);
                  const customerLabel = order.customerName?.trim() || tableName || 'Sin nombre';
                  const fulfillment = FULFILLMENT_LABELS[order.fulfillmentType ?? order.destination ?? ''] ?? '—';
                  return (
                    <button
                      key={order.id}
                      className="w-full px-5 py-4 text-left hover:bg-muted/30 transition"
                      onClick={() => setDetailOrder(order)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-text truncate">{customerLabel}</p>
                          <p className="text-xs text-text-muted mt-0.5">{formatDateTime(order.createdAt)} · {fulfillment}</p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="font-bold text-text">{formatCOP(order.total ?? 0)}</p>
                          <div className="flex gap-1 mt-1 justify-end">
                            <KitchenBadge status={order.status} />
                            <PaymentBadge status={order.payment_status} />
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-muted transition hover:bg-muted disabled:opacity-40"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-medium text-text-muted">
              Página {page} de {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-muted transition hover:bg-muted disabled:opacity-40"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}

      </div>

      {/* Detail modal */}
      <OrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} />
    </div>
  );
}
